import { useEffect, useState } from "react";
import { Button, DatePicker, Input, Select, Tag } from "antd";
import moment from "moment";
import { GetTokenDecoded } from "../../../utils/storeUsuario";
import { ControllerListarSegurosClinica } from "../../../Service/Services.seguros";
import { ListarDoctorClinica } from "../../../Service/Service.doctor";
import { ListadoPlanes } from "../../../Service/Service.Plan";
import { chipsAplicados, BANDEJA_FILTROS_VACIOS } from "../../../utils/auditoriaBandeja";

const { RangePicker } = DatePicker;

// AUDITORIA-CLINICA-REDISENO (FASE 3) -- filtros de la bandeja en UNA sola
// línea: buscador + rango de fechas con presets + "Más filtros". Lo aplicado
// vuelve como chips removibles, para que quitar un filtro sea un clic.
//
// Los catálogos (seguros/doctores/planes) se cargan con los MISMOS servicios
// que ya usa `AuditoriaClinicaFiltros.js`; no se agrega ningún endpoint.
const COBERTURA_OPCIONES = [
    { value: "PARTICULAR", label: "Particular" },
    { value: "SEGURO", label: "Con seguro" },
    { value: "SIN_DETERMINAR", label: "Sin determinar" },
];

const PRESETS_FECHA = [
    { label: "Hoy", value: [moment().startOf("day"), moment().endOf("day")] },
    { label: "Últimos 7 días", value: [moment().subtract(6, "days").startOf("day"), moment().endOf("day")] },
    { label: "Últimos 30 días", value: [moment().subtract(29, "days").startOf("day"), moment().endOf("day")] },
    { label: "Este mes", value: [moment().startOf("month"), moment().endOf("month")] },
];

function BandejaFiltros({ filtros, onCambio, onLimpiar }) {
    const [seguros, setSeguros] = useState([]);
    const [doctores, setDoctores] = useState([]);
    const [planes, setPlanes] = useState([]);
    const [masFiltros, setMasFiltros] = useState(false);
    const [texto, setTexto] = useState(filtros.q || "");

    useEffect(() => {
        (async () => {
            const idclinica = GetTokenDecoded()?.idclinica;
            const [resSeguros, resDoctores, resPlanes] = await Promise.all([
                ControllerListarSegurosClinica(idclinica),
                ListarDoctorClinica(idclinica),
                ListadoPlanes(),
            ]);
            setSeguros(Array.isArray(resSeguros) ? resSeguros : resSeguros?.data || []);
            setDoctores(resDoctores?.data || []);
            setPlanes(resPlanes?.data || []);
        })();
    }, []);

    // El texto se aplica al confirmar (Enter o lupa), no en cada tecla: cada
    // aplicación es una consulta al servidor.
    useEffect(() => setTexto(filtros.q || ""), [filtros.q]);

    const etiquetas = {
        cobertura: COBERTURA_OPCIONES.find((c) => c.value === filtros.cobertura)?.label,
        aseguradora: seguros.find((s) => s.idseguro === filtros.aseguradora)?.nombre,
        plan: planes.find((p) => p.id_plan === filtros.plan)?.nombre,
        doctor: doctores.find((d) => d.iddoctor === filtros.doctor)?.nombre,
    };
    const chips = chipsAplicados(filtros, etiquetas);

    return (
        <div className="ac-bandeja__filtros" data-testid="ac-bandeja-filtros">
            <div className="ac-filtros-linea">
                <Input.Search
                    allowClear
                    className="ac-filtros-buscador"
                    placeholder="Paciente, documento o #Nº de atención"
                    value={texto}
                    onChange={(e) => setTexto(e.target.value)}
                    onSearch={(v) => onCambio({ q: v })}
                    data-testid="ac-bandeja-buscador"
                />
                <RangePicker
                    format="DD/MM/YYYY"
                    ranges={PRESETS_FECHA.reduce((acc, p) => ({ ...acc, [p.label]: p.value }), {})}
                    value={filtros.rango}
                    onChange={(v) => onCambio({ rango: v })}
                    data-testid="ac-bandeja-rango"
                />
                <Button onClick={() => setMasFiltros((v) => !v)} data-testid="ac-bandeja-mas-filtros">
                    {masFiltros ? "Menos filtros" : "Más filtros"}
                </Button>
            </div>

            {masFiltros && (
                <div className="ac-filtros-linea" data-testid="ac-bandeja-filtros-extra">
                    <Select
                        allowClear
                        className="ac-filtros-select"
                        placeholder="Cobertura"
                        options={COBERTURA_OPCIONES}
                        value={filtros.cobertura}
                        onChange={(v) => onCambio({ cobertura: v })}
                    />
                    <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        className="ac-filtros-select"
                        placeholder="Aseguradora"
                        options={seguros.map((s) => ({ value: s.idseguro, label: s.nombre }))}
                        value={filtros.aseguradora}
                        onChange={(v) => onCambio({ aseguradora: v })}
                    />
                    <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        className="ac-filtros-select"
                        placeholder="Plan"
                        options={planes.map((p) => ({ value: p.id_plan, label: p.nombre }))}
                        value={filtros.plan}
                        onChange={(v) => onCambio({ plan: v })}
                    />
                    <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        className="ac-filtros-select"
                        placeholder="Médico"
                        options={doctores.map((d) => ({ value: d.iddoctor, label: d.nombre }))}
                        value={filtros.doctor}
                        onChange={(v) => onCambio({ doctor: v })}
                    />
                </div>
            )}

            {chips.length > 0 && (
                <div className="ac-filtros-chips" data-testid="ac-bandeja-chips">
                    {chips.map((chip) => (
                        <Tag
                            key={chip.campo}
                            closable
                            onClose={(e) => {
                                e.preventDefault();
                                onCambio({ [chip.campo]: BANDEJA_FILTROS_VACIOS[chip.campo] });
                            }}
                            data-testid={`ac-chip-${chip.campo}`}
                        >
                            {chip.texto}
                        </Tag>
                    ))}
                    <Button type="link" size="small" onClick={onLimpiar} data-testid="ac-bandeja-limpiar">
                        Limpiar
                    </Button>
                </div>
            )}
        </div>
    );
}

export default BandejaFiltros;
